import { type CSSObject } from '@emotion/react';
import { keyframes } from '@mui/material/styles';

import { useTextFieldStyles } from '../textField.styles';
import { type Theme, useTheme } from '../../../../theme';
import { type InputProps } from './input';

export type InputUseStyleProps = {
  size?: InputProps['size'];
  isFilled?: boolean;
  hasError?: boolean;
  disabled?: boolean;
  contentEditable?: boolean;
};

const fadePulseRed = (theme: Theme): string => keyframes`
  0% {
    color: ${theme.palette.neutral.grey[500]};
  }
  50% {
    color: ${theme.palette.error.main};
  }
  100% {
    color: ${theme.palette.neutral.grey[500]};
  }
`;

type InputStyles = Record<
  | 'muiTextFieldCSS'
  | 'inputFilledIconWrapperCSS'
  | 'inputCharacterCountPulseRedCSS'
  | 'endAdornmentIconWrapperCSS',
  CSSObject
>;

export const useInputStyles = ({
  size = 'small',
  isFilled = false,
  hasError = false,
  disabled,
  contentEditable,
}: InputUseStyleProps): InputStyles => {
  const theme = useTheme();
  const { sizeVariant, root } = useTextFieldStyles({
    isFilled,
    hasError,
    disabled,
    contentEditable,
    size,
  });

  return {
    muiTextFieldCSS: {
      ...root,
      // make the entire native date input clickable (not just the calendar icon)
      // this makes it easier to open the date picker on mobile
      '& input[type="date"]::-webkit-calendar-picker-indicator': {
        color: 'transparent',
        cursor: 'pointer',
        position: 'absolute',
        background: 'transparent',
        width: '100%',
        height: 'auto',
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 2,
      },
      '& input::-webkit-date-and-time-value': {
        textAlign: 'left',
        height: 'auto',
      },
    },
    inputFilledIconWrapperCSS: {
      color: theme.icon.secondary,
      cursor: 'pointer',
      '&: hover': {
        color: theme.iconButton.icon.default,
      },
      ...sizeVariant.endAdornmentIconWrapperCSS,
    },
    inputCharacterCountPulseRedCSS: {
      animation: `${fadePulseRed(theme)} 1s ${
        theme.transitions.easing.easeInOut
      }`,
    },
    endAdornmentIconWrapperCSS: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,

      [`&:not(:has(button))`]: {
        paddingRight: theme.spacing(1),
      },
    },
  };
};
